// A/B Testing passcode — client side of server/services/ab_testing/abOtpService.js.
//
// Every create, edit or delete of an A/B Testing campaign asks for the passcode
// first (ensureAbOtp). Entering it opens a 30-minute server-side edit window for
// that one campaign. If a save still comes back 403 AB_OTP_REQUIRED (the window
// expired mid-edit), the axios interceptor installed below asks again and
// retries the request once, so no save path has to handle it itself.
//
// Whether the layer is on is decided by the SERVER (AB_TESTING_OTP_ENABLED=true
// turns it on), so switching it needs no client rebuild.
import axios from 'axios';

let promptImpl = null;
let statusPromise = null;

/** Called by <AbOtpPromptHost/>: `(campaignId, action) => Promise<boolean>`. */
export function registerAbOtpPrompt(fn) {
    promptImpl = fn;
    return () => { if (promptImpl === fn) promptImpl = null; };
}

const baseUrl = (appUrl) => appUrl || process.env.REACT_APP_BASE_URL || '';

/**
 * Whether the passcode layer is on. Cached per page load. A failed check counts
 * as "off": the server still enforces the passcode when it is on, and its 403
 * AB_OTP_REQUIRED brings up the prompt through the interceptor below.
 */
export function abOtpRequired(appUrl) {
    if (!statusPromise) {
        statusPromise = axios.get(`${baseUrl(appUrl)}/api/ab-testing/otp/status`, { withCredentials: true })
            .then(({ data }) => data?.required === true)
            .catch(() => { statusPromise = null; return false; });
    }
    return statusPromise;
}

export async function verifyAbOtp(appUrl, { campaignId, action, otp }) {
    const { data } = await axios.post(`${baseUrl(appUrl)}/api/ab-testing/otp/verify`, { campaignId, action, otp }, { withCredentials: true });
    return data;
}

/**
 * Resolve true when the user may go ahead with `action` on `campaignId`:
 * immediately when the layer is off, otherwise once the passcode is accepted.
 * Resolves false when they cancel.
 */
export async function ensureAbOtp(appUrl, campaignId, action = 'edit') {
    if (!campaignId) return false;
    if (!(await abOtpRequired(appUrl))) return true;
    if (!promptImpl) return false;
    return promptImpl(String(campaignId), action);
}

let interceptorInstalled = false;

/** Retry a write that the server refused for a missing/expired passcode window, once. */
export function installAbOtpInterceptor() {
    if (interceptorInstalled) return;
    interceptorInstalled = true;
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const res = error?.response;
            const config = error?.config;
            if (res?.status !== 403 || res?.data?.code !== 'AB_OTP_REQUIRED' || !config || config.__abOtpRetried || !promptImpl) {
                return Promise.reject(error);
            }
            const ok = await promptImpl(String(res.data.campaign_id || ''), 'edit');
            if (!ok) return Promise.reject(error);
            return axios({ ...config, __abOtpRetried: true });
        },
    );
}
